<template>
  <div class="flex flex-col gap-y-3">
    <div class="w-full flex justify-between">
      <slot name="header" :header="dashboardHeader">
        <h1 class="text-xl md:text-2xl text-primary font-medium">
          {{ $t('controlPanel') }}
        </h1>
      </slot>
    </div>
    <Form
      :initial-values="initialValues"
      class="w-full flex gap-3 items-end"
      v-slot="{ values, resetField }"
    >
      <Field :rules="requiredRule" name="fromDate" v-slot="{ field, errors: errs, handleChange }">
        <DatepickerV2
          :key="values.toDate ?? new Date()"
          :field="field"
          :errors="errs"
          :label="$t('fromDate')"
          :default-value="field.value"
          :server-date-format="DATE_FORMAT"
          :max-date="values.toDate ?? '__now__'"
          input-class="ps-3 py-0.5"
          class="w-1/2 md:w-1/4"
          @on-change="
            (date) => {
              handleChange(date)
              resetField('toDate')
            }
          "
        >
        </DatepickerV2>
      </Field>
      <Field :rules="requiredRule" name="toDate" v-slot="{ field, errors: errs, handleChange }">
        <DatepickerV2
          :key="values.fromDate"
          :field="field"
          :errors="errs"
          :label="$t('toDate')"
          :default-value="field.value"
          :server-date-format="DATE_FORMAT"
          :min-date="values.fromDate"
          max-date="__now__"
          input-class="ps-3 py-0.5"
          class="w-1/2 md:w-1/4"
          @on-change="(date) => handleChange(date)"
        >
        </DatepickerV2>
      </Field>
      <button
        class="pe-1.5 mb-1.5 rounded-md bg-primary text-white flex items-center hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed"
        @click.prevent="handleDateSearch(values)"
      >
        <div class="flex justify-center items-center h-full p-2 rounded-s bg-black/10">
          <faMagnifyingGlass class="w-3 fill-white" />
        </div>
        <span class="px-2 tracking-wide text-white text-sm pb-[1px]">
          {{ $t('search') }}
        </span>
      </button>
    </Form>
    <div class="w-full grid md:grid-cols-3 lg:grid-cols-4 gap-4">
      <template v-if="isLoading">
        <div
          :key="i"
          v-for="i in [1, 2, 2, 2, 2, 2, 2, 2, 2]"
          class="w-full min-h-[12rem] bg-primary/10 border border-primary flex flex-col gap-y-2 p-4 animate-pulse shadow-md"
        >
          <div class="w-20 h-20 rounded-full bg-primary/40"></div>
          <div class="w-full rounded-md py-2 bg-primary/40"></div>
          <div class="w-[86%] rounded-md py-2 bg-primary/40"></div>
        </div>
      </template>
      <template v-else>
        <component
          :key="key"
          :is="'boxElement' in item ? Type2 : Type1"
          :item="item"
          class="w-full min-h-[12rem] border border-primary rounded-md flex flex-col gap-y-2 p-4 shadow-sm"
          v-for="(item, key) in dashboardItems"
        >
        </component>
      </template>
    </div>
  </div>
</template>

<script setup>
import faMagnifyingGlass from '@/assets/icons/faMagnifyingGlass.svg?component'
import Type1 from '@/components/Dashboard/ControlpanelCards/1.vue'
import Type2 from '@/components/Dashboard/ControlpanelCards/2.vue'
import DatepickerV2 from '@/components/SimpleForm/DatepickerV2.vue'
import useFormatDate from '@/composables/useFormatDate'
import axios from '@/services'
import { DateTime } from 'luxon'
import { Field, Form } from 'vee-validate'
import { nextTick, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const isLoading = ref(true)
const { formateDateFromDDMMYYYY } = useFormatDate()
// const dateFormat = 'yyyy-MM-dd'
const DATE_FORMAT = 'yyyy-MM-dd'

// const formatDate = (date) => DateTime.fromISO(date).toFormat(dateFormat)

const initialValues = {
  fromDate: DateTime.now().minus({ days: 30 }).toFormat(DATE_FORMAT),
  toDate: DateTime.now().toFormat(DATE_FORMAT)
}

const dashboardItems = ref()
const dashboardHeader = ref()

const dateFilter = reactive({
  fromDate: null,
  toDate: null
})

onMounted(async () => {
  await nextTick()
  await fetchDashboardItems(initialValues)
  isLoading.value = false
})

function requiredRule(value) {
  if (!value || !`${value}`.trim()) {
    return t('INVALID_REQUIRED')
  }

  return true
}

function extractPath(path) {
  const regex = /^#\/(.+?)(?:\?.*)?$/
  const match = regex.exec(path)
  return match ? match[1] : path.slice(1) // Remove # if no match
}

function keepState(fieldKey, value) {
  dateFilter[fieldKey] = value
}

async function fetchDashboardItems(payload = {}) {
  // const { fromDate, toDate } = payload
  // const params = {
  //   fromDate: formatDate(fromDate),
  //   toDate: formatDate(toDate)
  // }
  const res = await axios.get(`/api/dashboard/establishment`, {
    params: payload
  })
  dashboardItems.value = res
  dashboardHeader.value = {
    blackPoints: res.blackPoints,
    lastSyncDateTime: res.lastSyncDateTime
  }

  dashboardItems.value = [
    {
      eChannelsIcon: 'fa fa-user',
      value: res.numberOfEmployees,
      text: t('sponsored'),
      to: {
        name: 'EstSponsoredRequests'
      }
    },
    {
      eChannelsIcon: 'fa fa-file-text-o',
      value: res.numberOfVisaRequests,
      text: t('numberOfVisaRequests'),
      to: {
        name: 'EstMyRequests'
      }
    },
    {
      eChannelsIcon: 'fa fa-file-text-o',
      value: res.numberOfResidencyRequests,
      text: t('numberOfResidencyRequests'),
      to: {
        name: 'EstSponsoredRequests'
      }
    },
    {
      eChannelsIcon: 'fa fa-globe',
      boxElement: [
        {
          value: res.numberOfActiveVisas,
          text: t('numberOfActiveVisas'),
          dashboardElementTypes: { id: 1 },
          to: {
            name: 'EstSponsoredRequests',
            query: {
              moduleId: 2,
              serviceStatusId: 9,
              visaOrResiFileStatus: 0
            }
          }
        },
        {
          value: res.numberOfAlmostExpiredVisas,
          text: t('numberOfAlmostExpiredVisas'),
          dashboardElementTypes: { id: 3 },
          to: {
            name: 'EstSponsoredRequests',
            query: {
              moduleId: 2,
              serviceStatusId: 0,
              visaOrResiFileStatus: 6
            }
          }
        },
        {
          value: res.numberOfExpiredVisas,
          text: t('numberOfExpiredVisas'),
          dashboardElementTypes: { id: 4 },
          to: {
            name: 'EstSponsoredRequests',
            query: {
              moduleId: 2,
              serviceStatusId: 31,
              visaOrResiFileStatus: 0
            }
          }
        }
      ]
    },
    {
      eChannelsIcon: 'fa fa-globe',
      boxElement: [
        {
          value: res.numberOfActiveResidencies,
          text: t('numberOfActiveResidencies'),
          dashboardElementTypes: { id: 1 },
          to: {
            name: 'EstSponsoredRequests',
            query: {
              moduleId: 1,
              serviceStatusId: 2,
              visaOrResiFileStatus: 0
            }
          }
        },
        {
          value: res.numberOfAlmostExpiredResidencies,
          text: t('numberOfAlmostExpiredResidencies'),
          dashboardElementTypes: { id: 3 },
          to: {
            name: 'EstSponsoredRequests',
            query: {
              moduleId: 1,
              serviceStatusId: 0,
              visaOrResiFileStatus: 6
            }
          }
        },
        {
          value: res.numberOfExpiredResidencies,
          text: t('numberOfExpiredResidencies'),
          dashboardElementTypes: { id: 4 },
          to: {
            name: 'EstSponsoredRequests',
            query: {
              moduleId: 1,
              serviceStatusId: 4,
              visaOrResiFileStatus: 0
            }
          }
        }
      ]
    },
    {
      eChannelsIcon: 'fa fa-exchange',
      value: 0,
      text: t('numberOfExitEntry'),
      hideNumber: true,
      to: { name: 'ServiceForm', params: { serviceId: 369 } }
    },
    {
      eChannelsIcon: 'fa fa-money',
      value: res.totalAmount,
      text: t('financialTransaction'),
      to: { name: 'payments' }
    },
    {
      eChannelsIcon: 'fa fa-file-text-o',
      value: res.numberOfReadyToPayDrafts,
      text: t('numberOfReadyToPayDrafts'),
      to: { name: 'EstRequestsForPayment' }
    },
    {
      eChannelsIcon: 'fa fa-globe',
      boxElement: [
        {
          value: res.numberOfCompletedWorkPackageRequest,
          text: t('completedWorkPackageRequest'),
          to: {
            name: 'EstMyRequests',
            query: {
              moduleId: 1,
              searchFromDate: dateFilter.fromDate,
              searchToDate: dateFilter.toDate,
              workPackageStatus: 1
            }
          }
        },
        {
          value: res.numberOfInprogressWorkPackageRequest,
          text: t('inProgressWorkPackageRequest'),
          to: {
            name: 'EstMyRequests',
            query: {
              moduleId: 1,
              searchFromDate: dateFilter.fromDate,
              searchToDate: dateFilter.toDate,
              workPackageStatus: 2
            }
          }
        }
      ]
    }
  ]
}

const handleDateSearch = async (values) => {
  fetchDashboardItems(values)
}
</script>

<style lang="scss" scoped></style>
